import { Component, OnInit, OnDestroy, Input, inject } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { PostTradeService } from '../../post-trade.service';
import { CaseManagementService } from '../../../case-management/case-management.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { Router } from '@angular/router';
import Highcharts from 'highcharts';
import {
  accountKey,
  accountHistoryKey,
  accountReviewKey,
  accountInfoKey,
  suitabilityKey,
  accountHoldingsKey,
  recentActivityKey,
  caseHistoryKey,
  transactionHistoryKey,
  preTradeAccordionKey,
} from './account-review-detail-keys';
import { tradePositionKey } from '../../trade-review/trade-review-detail/trade-review-detail-keys';
import { scoreClassKey } from '../../../lib/shared-value-keys';
import { MatSnackBar } from '@angular/material/snack-bar';
import isEmpty from 'lodash-es/isEmpty';
import { MatDialog } from '@angular/material/dialog';
import { AccountHistoryDialogComponent } from './account-history-dialog/account-history-dialog.component';
import { ExternalGroupingDialogComponent } from '../../account-grouping/external-grouping-dialog/external-grouping-dialog.component';
import { ClipboardService } from 'ngx-clipboard';
import { CaseCreationDialogComponent } from '../../components/case-creation-dialog/case-creation-dialog.component';
import { TagsEvaluationService } from '../../../services/tags-evaluation.service';
import { PostTradeDetailResponse } from '../../../models/post-trade-models';
import { RightBridgeApiService } from '../../../services/right-bridge-api.service';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';

import { ApprovalMenuComponent } from '../../components/approval-menu/approval-menu.component';
import { AttachmentsWidgetComponent } from '../../components/attachments-widget/attachments-widget.component';
import { ExpandingTableComponent } from '../../components/expanding-table/expanding-table.component';
import { CardInfopointComponent } from '../../components/card-infopoint/card-infopoint.component';
import { HoldingsCardComponent } from '../../components/holdings-card/holdings-card.component';
import { MultiTableCardComponent } from '../../components/multi-table-card/multi-table-card.component';
import { ReasonTextComponent } from '../../../shared-components/reason-text/reason-text.component';
import { NgxLoadingModule } from 'ngx-loading';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
  selector: 'app-account-review-detail',
  templateUrl: './account-review-detail.component.html',
  styleUrls: ['./account-review-detail.component.scss'],
  imports: [
    ApprovalMenuComponent,
    AttachmentsWidgetComponent,
    ExpandingTableComponent,
    CardInfopointComponent,
    HoldingsCardComponent,
    MultiTableCardComponent,
    ReasonTextComponent,
    NgxLoadingModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatTabsModule,
    MatExpansionModule,
  ],
})
export class AccountReviewDetailComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  fb = inject(FormBuilder);
  ptSvc = inject(PostTradeService);
  cms = inject(CaseManagementService);
  ss = inject(SessionStorageService);
  private route = inject(Router);
  private location = inject(Location);
  private snacky = inject(MatSnackBar);
  private tes = inject(TagsEvaluationService);
  private rbs = inject(RightBridgeApiService);
  private clippy = inject(ClipboardService);

  @Input() accountId;
  rights = [];
  accountDetail;
  accountKey = accountKey;
  accountHistoryKey = accountHistoryKey;
  accountReviewKey = accountReviewKey;
  accountInfoKey = accountInfoKey;
  suitabilityKey = suitabilityKey;
  accountHoldingsKey = accountHoldingsKey;
  recentActivityKey = recentActivityKey;
  scoreClassKey = scoreClassKey;
  caseHistoryKey = caseHistoryKey;
  tradePositionKey = tradePositionKey;
  transactionHistoryKey = transactionHistoryKey;
  preTradeAccordionKey = preTradeAccordionKey;
  isEmpty = isEmpty;
  accountHoldingsRows = 5;
  recentActivityRows = 5;
  caseHistory = [];
  caseHistoryRows = 5;
  reviewCase = false;
  caseChangesMade = false;
  caseData;
  aiSummary = [];
  selectedActionTab = 0;

  Highcharts: typeof Highcharts = Highcharts;
  unsubscribe: Subject<any> = new Subject();
  loading = false;
  softLoading = false;

  ngOnInit(): void {
    this.loading = true;
    this.rights = this.ss.get('rights');
    this.ptSvc
      .getAccountDetail(this.accountId)
      .subscribe((resp: PostTradeDetailResponse) => {
        if (resp.Details?.bridges) {
          resp.Details.bridges.forEach(bridge => {
            if (bridge.trueRules) {
              bridge.trueRules.forEach(rule => {
                rule.explanation =
                  rule.reasonType === 'Negative'
                    ? this.tes.evalTags(rule.explanation)
                    : '';
                rule.headline =
                  rule.reasonType === 'Negative'
                    ? this.tes.evalTags(rule.headline)
                    : '';
                bridge.negativeReason =
                  rule.reasonType === 'Negative' ? true : bridge.negativeReason;
              });
            }
          });
        }

        this.accountDetail = resp;
        this.loading = false;
      });

    this.cms
      .getRelatedCases(this.accountId, ['Account', 'AccountGroup'])
      .subscribe(resp => {
        if (resp['length'] > 0) {
          Object.keys(resp).map(index => {
            this.caseHistory.push(resp[index]);
          });
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  returnZero() {
    return 0;
  }

  nav(location) {
    this.route.navigate([`/${location}`]);
  }

  toggleReviewed(reviewStatus) {
    this.loading = true;

    this.ptSvc
      .updateAccount({
        AccountID: [this.accountId],
        ApprovalStatus: reviewStatus,
      })
      .subscribe(() => {
        this.ptSvc
          .getAccountDetail(this.accountId)
          .subscribe((resp: PostTradeDetailResponse) => {
            this.snacky.open(`${this.accountId} was updated!`, 'Close', {
              duration: 4000,
            });

            this.accountDetail = resp;
            this.loading = false;
          });
      });
  }

  initiateCase() {
    const alerts = [];

    if (
      this.accountDetail?.Details?.bridges &&
      this.accountDetail?.Details?.bridges.length > 0
    ) {
      this.accountDetail.Details.bridges[0].trueRules.map(alert => {
        if (alert.score < 0) {
          alerts.push(alert);
        }
      });
    }

    const confirmationDialog = this.dialog.open(CaseCreationDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        alerts: alerts,
      },
    });

    confirmationDialog.afterClosed().subscribe(resp => {
      if (resp?.action === 'continue') {
        this.loading = true;
        const caseData = {
          CaseManagement: {
            Type: 'Account',
            Title: this.accountId,
            ClientName: this.accountDetail.ClientFullName,
            LowestScore: this.accountDetail.ScoreClass,
          },
          CaseToProfile: {
            ProfileID: [this.accountId],
          },
          Client_Rep: {
            RepCode: [this.accountDetail.RepCode],
          },
        };

        if (resp?.caseAlerts.length > 0) {
          caseData['PostTradeAlerts'] = { AlertIds: resp.caseAlerts };
        }

        if (resp?.assignedTo) {
          caseData['CaseManagement']['AssignedTo'] = resp?.assignedTo;
        }

        if (resp?.repName) {
          caseData['CaseManagement']['AssignedToName'] = resp?.repName;
        }

        this.cms
          .createCaseManagementRecord(this.accountId, caseData)
          .subscribe(resp => {
            this.loading = false;
            const note = {
              CaseRecordId: resp['result'],
              NoteText: `A case has been initiated for the selected account.
          Please provide any necessary notes or actions.<br/>`,
              NoteTypeId: 3,
            };

            if (this.accountDetail.ClientFullName) {
              note.NoteText =
                note.NoteText +
                `<br/>Client Name: ${this.accountDetail.ClientFullName}`;
            }

            if (this.accountDetail.AccountNumDisplay) {
              note.NoteText =
                note.NoteText +
                `<br/>Account #: ${this.accountDetail.AccountNumDisplay}`;
            }

            if (
              this.accountDetail.Details.Account &&
              this.accountDetail.Details.Account.WorkID
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Work/Application #: ${this.accountDetail.Details.Account.WorkID}`;
            }

            if (
              this.accountDetail.Details.Account &&
              this.accountDetail.Details.Account.OpenDate
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Account Established Date: ${this.accountDetail.Details.Account.OpenDate}`;
            }

            if (
              this.accountDetail.Details.Display &&
              this.accountDetail.Details.Display.AccountType
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Account Type: ${this.accountDetail.Details.Display.AccountType}`;
            }

            if (
              this.accountDetail.Details.Display &&
              this.accountDetail.Details.Display.Registration
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Registration: ${this.accountDetail.Details.Display.Registration}`;
            }

            if (
              this.accountDetail.Details.Display &&
              this.accountDetail.Details.Display.TaxStatus
            ) {
              note.NoteText =
                note.NoteText +
                `<br/>Tax Status: ${this.accountDetail.Details.Display.TaxStatus}`;
            }

            note.NoteText = note.NoteText + `<br/>~${this.accountId}`;

            this.cms.addCaseRecordNote(note).subscribe(
              () => {
                this.snacky.open('Case created & note saved', 'Close', {
                  duration: 2000,
                });
              },
              error => {
                this.snacky.open(`Error: ${error}`);
              }
            );

            this.nav(`case-management/case-manager/${resp['result']}`);
          });
      }
    });
  }

  viewAccountReviewHistory() {
    this.dialog.open(AccountHistoryDialogComponent, {
      data: {
        accountDetail: this.accountDetail,
      },
      width: '80vw',
      maxWidth: '80vw',
    });
  }

  toggleCaseReview(caseItem) {
    this.cms
      .getCaseManagementProfileData(caseItem.CRID, 'FrontEnd')
      .subscribe(() => {
        // Leaving this here in case we want to embed the case manager later/maybe for another configuration?
        // this.caseData = caseItem;
        // this.reviewCase = !this.reviewCase;
        window.open(
          `${this.location.prepareExternalUrl('/case-management/case-manager/')}${
            caseItem.CRID
          }`,
          '_blank'
        );
      });
  }

  toggleRows(rowProp, value) {
    if (this[rowProp] !== value) {
      this[rowProp] = value;
    } else {
      this[rowProp] = 5;
    }
  }

  changeCaseValue(value) {
    this.caseChangesMade = value;
  }

  accountGrouping() {
    this.dialog.open(ExternalGroupingDialogComponent, {
      data: {
        externalInitData: {
          FullName: this.accountDetail.AccountProfile.ClientPerson.Full_Name,
          AccountID_Rev: this.accountDetail.AccountID_Rev,
          HashedSSN: this.accountDetail.AccountProfile.ClientPerson.HashedSSN,
          ROANumber: this.accountDetail.ROANumber,
          CumulativeDiscountNum: this.accountDetail.CumulativeDiscountNum,
          GroupID: this.accountDetail.GroupID,
          GroupDescription: this.accountDetail.GroupDescription,
        },
      },
      width: '80vw',
      maxWidth: '80vw',
      autoFocus: false,
    });
  }

  hasAppProducts(varset, profileId) {
    if (
      this.accountDetail.Details[varset] &&
      this.accountDetail.Details[varset].find(
        product => product.ProfileID === profileId
      )
    ) {
      return true;
    }
    return false;
  }

  decryptValue(value: string, title: string) {
    this.softLoading = true;
    this.rbs.decryptValue(value).subscribe({
      next: resp => {
        this.softLoading = false;
        if (resp['resultString']) {
          let dialogRef = this.dialog.open(WarnDialogComponent, {
            panelClass: 'warn-dialog',
            data: {
              headline: title,
              content: `Value: ${resp['resultString']}`,
              confirm: 'Copy to Clipboard',
              cancel: 'Close',
              hideCancel: false,
            },
          });
          dialogRef.afterClosed().subscribe(result => {
            if (result === 'continue') {
              this.clippy.copy(resp['resultString']);
              this.snacky.open(`Copied to clipboard!`, 'Close', {
                duration: 4000,
              });
            }
            dialogRef = null;
          });
        }
      },
      error: err => {
        this.softLoading = false;
        console.error('ERROR: ', err);
        this.snacky.open(
          `Oops! Something went wrong. Please try again.`,
          'Close',
          {
            duration: 4000,
          }
        );
      },
    });
  }
}
